import { useEffect } from "react";
import { NavLink } from "react-router";
import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "./VenueContext";
import { useVenueEntitlements } from "./entitlements";
import { useGuaranteePolicy } from "./guaranteePolicy";
import { NAV_ENTRIES, NAV_SECTIONS, SECTION_LABEL_KEYS, type NavEntry } from "./nav";
import { canAccessVenue } from "./roles";
import { ShellIcon } from "./icons";

/**
 * Role-filtered primary nav, grouped into labelled sections. Entries are filtered through the SAME
 * `canAccessVenue` the route guard uses, so a link the user cannot enter is never shown (nav and
 * guards can't disagree). Each visible entry is bucketed by its `section` and rendered under a section
 * heading in `NAV_SECTIONS` order — an empty section (all its links filtered out) renders nothing.
 * `<nav>` + `aria-current` from NavLink give the landmark + active state RGAA expects.
 *
 * Dual-mode: a static left sidebar at `md`+; below `md` it's an off-canvas drawer toggled from the
 * header ([open]/[onClose]) — the shell closes it on navigation, so a link tap dismisses it. A backdrop
 * and Escape also close it. Above `md` the `open` state is inert (CSS pins the sidebar static/visible).
 */
export function SideNav({ open = false, onClose }: { open?: boolean; onClose?: () => void } = {}) {
  const { venueId } = useVenue();
  const { claims } = useAuth();
  const { has } = useVenueEntitlements();
  const { noShowEnabled, depositEnabled } = useGuaranteePolicy();
  const { t } = useTranslation();

  // Escape closes the mobile drawer (only bound while open).
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  // Role is a HIDE gate (a waiter can't unlock manager access); a missing entitlement is an EXPLAIN
  // gate — the entry stays visible, rendered disabled with a lock, so the plan feature is discoverable.
  // The no-show guarantee flags are a HARD-HIDE (unlike entitlements): an output surface that only exists
  // once the owner enables the policy is removed entirely rather than shown locked.
  const visible = NAV_ENTRIES.filter(
    (entry) =>
      canAccessVenue(claims, venueId, entry.minRole) &&
      (!entry.requiresNoShowPolicy || noShowEnabled) &&
      (!entry.requiresDepositPolicy || depositEnabled),
  );
  const bySection = new Map<string, NavEntry[]>();
  for (const entry of visible) {
    if (!entry.section) continue;
    const list = bySection.get(entry.section) ?? [];
    list.push(entry);
    bySection.set(entry.section, list);
  }

  return (
    <>
      {/* Drawer backdrop — a real button so tap/Enter/Space all dismiss it (a11y). Below md only. */}
      {open && (
        <button
          type="button"
          aria-label={t("shell.nav.close")}
          onClick={onClose}
          className={css({
            display: { base: "block", md: "none" },
            position: "fixed",
            inset: "0",
            bg: "rgba(0,0,0,0.4)",
            zIndex: "40",
            border: "none",
            cursor: "pointer",
          })}
        />
      )}
      <nav
        id="app-sidenav"
        aria-label={t("shell.nav.label")}
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "5",
          py: "4",
          px: "3",
          borderRightWidth: "1px",
          borderColor: "border.default",
          bg: "bg.subtle",
          minW: "60",
          overflowY: "auto",
          // Static sidebar at md+; off-canvas drawer below md.
          position: { base: "fixed", md: "static" },
          insetBlock: { base: "0", md: "auto" },
          insetInlineStart: { base: "0", md: "auto" },
          zIndex: { base: "50", md: "auto" },
          boxShadow: { base: open ? "lg" : "none", md: "none" },
          transform: {
            base: open ? "translateX(0)" : "translateX(-100%)",
            md: "translateX(0)",
          },
          transition: "transform 0.2s ease",
        })}
      >
        {NAV_SECTIONS.map((section) => {
          const entries = bySection.get(section);
          if (!entries || entries.length === 0) return null;
          return (
            <div
              key={section}
              className={css({ display: "flex", flexDirection: "column", gap: "1" })}
            >
              <h2
                className={css({
                  px: "3",
                  mb: "1",
                  fontSize: "2xs",
                  fontWeight: "semibold",
                  letterSpacing: "wider",
                  textTransform: "uppercase",
                  color: "fg.subtle",
                })}
              >
                {t(SECTION_LABEL_KEYS[section])}
              </h2>
              {entries.map((entry) => {
                const locked = typeof entry.feature === "string" && !has(entry.feature);
                return (
                  <NavLink
                    key={entry.to}
                    to={`/v/${venueId}/${entry.to}`}
                    end={entry.to === "overview"}
                    title={locked ? t("nav.locked") : undefined}
                    className={({ isActive }: { isActive: boolean }) => {
                      const active = isActive && !locked;
                      return css({
                        display: "flex",
                        alignItems: "center",
                        gap: "2.5",
                        px: "3",
                        py: "2",
                        rounded: "md",
                        fontSize: "sm",
                        fontWeight: active ? "semibold" : "medium",
                        bg: active ? "colorPalette.subtle" : "transparent",
                        color: locked ? "fg.subtle" : active ? "colorPalette.text" : "fg.muted",
                        borderLeftWidth: "2px",
                        borderColor: active ? "colorPalette.default" : "transparent",
                        transition: "background 0.12s, color 0.12s",
                        _hover: { bg: "bg.muted", color: locked ? "fg.muted" : "fg.default" },
                      });
                    }}
                  >
                    <span
                      className={css({ fontSize: "md", display: "inline-flex", flexShrink: 0 })}
                    >
                      <ShellIcon name={entry.icon} />
                    </span>
                    <span className={css({ truncate: true, flex: "1" })}>{t(entry.labelKey)}</span>
                    {locked && (
                      <span
                        aria-label={t("nav.locked")}
                        className={css({ fontSize: "sm", color: "fg.subtle", flexShrink: 0 })}
                      >
                        <ShellIcon name="lock" />
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>
    </>
  );
}
